/**
 * **The trainer's clock** — the one place an instant becomes a day and an hour on the booking grid.
 *
 * Booking-system R3 states availability "on the trainer's clock": weekly windows and date overrides
 * are wall readings in the trainer's own zone, published by `GET /trainer-availability` as
 * `trainerTimeZoneId`. The grid is drawn in that zone (FullCalendar's `timeZone`), so every day this
 * slice names — which square a session sits in, which date an override applies to, what "today"
 * is — is read in that zone too, never in the browser's. A trainer who opens the app abroad sees
 * their own week, not the hotel's.
 *
 * `Intl` is read directly: the browser has the whole of ECMA-402, and the static tables the mobile
 * app keeps exist only because Hermes throws on a `timeZone`.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { dateTimeFormat } from '~/shared/lib/intlCache';
import { resolvableZone } from '~/shared/lib/occurrenceZone';

/** A civil day, `YYYY-MM-DD`. */
export type CivilDay = string;

/**
 * The server's own zone for a trainer it cannot place (`ResolveBookingZone`'s default). The grid
 * falls back to it — never to the browser's zone, which would draw the trainer's week on the clock
 * of wherever the laptop happens to be.
 */
export const DEFAULT_BOOKING_ZONE = 'Europe/Warsaw';

/**
 * The zone the booking screen reads everything in: the trainer's published zone when this browser
 * can format in it, else the server's default. Absent, and named-but-unknown to this runtime's tz
 * data, are the same answer.
 */
export function bookingZone(published: string | null | undefined): string {
  return resolvableZone(published) ?? DEFAULT_BOOKING_ZONE;
}

/**
 * Whose clock the grid is on, said out loud when it is not the reader's own (calendar-events
 * Definitions, "User's local day": whose day a surface shows must be legible to the person reading
 * it). The zone is named when the grid's zone differs from the browser's, and when the trainer
 * published none this browser can read — the grid then stands on the server's default, which the
 * reader has no other way to know. `null` when the two agree: nothing is said.
 */
export function zoneNotice(
  published: string | null | undefined,
  browserZone: string | null,
): string | null {
  const zone = bookingZone(published);
  const fellBack = resolvableZone(published) === null;
  return fellBack || zone !== browserZone ? zone : null;
}

/** Where an instant stands on the trainer's clock. */
export interface WallReading {
  readonly day: CivilDay;
  /** Minutes since that day's midnight, 0…1439. */
  readonly minutes: number;
}

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

/**
 * The instant `ms` read on `zone`'s wall clock.
 *
 * `en-CA` with `h23` because both are fixed shapes (`2026-09-24`, `0`–`23`) whatever the reader's
 * own locale; the parts are read by type, never by position.
 */
export function wallReading(ms: number, zone: string): WallReading {
  const parts = dateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? '';
  return {
    day: `${part('year')}-${part('month')}-${part('day')}`,
    minutes: Number(part('hour')) * 60 + Number(part('minute')),
  };
}

/** `H:MM` from minutes since midnight — the grid's and the agenda's clock. */
export function clockOf(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${String(hours)}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * `HH:MM` from minutes since midnight — the wire's `TimeOnly` and FullCalendar's time strings. 1440
 * and past it read as `24:00`: the end of a window that runs to midnight.
 */
export function paddedClock(minutes: number): string {
  if (minutes >= 1440) return '24:00';
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** `H:MM` of an instant on the trainer's clock. */
export function wallClock(ms: number, zone: string): string {
  return clockOf(wallReading(ms, zone).minutes);
}

/**
 * Minutes since midnight of a wire `TimeOnly` (`HH:mm` or `HH:mm:ss`), or `null` when it is not one.
 * A window ending at midnight arrives as `00:00` on some writers and is read as the END of the day
 * by the caller, which knows which end it is reading.
 */
export function minutesOfTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(value);
  if (match === null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * `day` moved by `count` civil days. Read and written in UTC on purpose: a civil day is not a zone
 * question, and UTC is the one clock with no daylight-saving gap to fall into.
 */
export function shiftDay(day: CivilDay, count: number): CivilDay {
  const at = Date.parse(`${day}T00:00:00Z`) + count * DAY_MS;
  return new Date(at).toISOString().slice(0, 10);
}

/** ISO weekday of a civil day, 1 (Monday) … 7 (Sunday) — the backend's `WeeklyWindow.DayOfWeek`. */
export function isoWeekday(day: CivilDay): number {
  const js = new Date(`${day}T00:00:00Z`).getUTCDay();
  return js === 0 ? 7 : js;
}

/** The Monday of the week `day` stands in. */
export function mondayOfDay(day: CivilDay): CivilDay {
  return shiftDay(day, 1 - isoWeekday(day));
}

/** Every civil day from `first` to `last`, both included. Empty when `last` precedes `first`. */
export function daysBetween(first: CivilDay, last: CivilDay): CivilDay[] {
  const days: CivilDay[] = [];
  for (let day = first; day <= last; day = shiftDay(day, 1)) days.push(day);
  return days;
}

/** A civil day as a `Date` at UTC noon — safe to hand to an `Intl` formatter set to `UTC`. */
export function civilDate(day: CivilDay): Date {
  return new Date(`${day}T12:00:00Z`);
}

/** Minutes between two instants, rounded up so "za 0 min" never appears for a session still ahead. */
export function minutesUntil(fromMs: number, toMs: number): number {
  return Math.ceil((toMs - fromMs) / MINUTE_MS);
}

/**
 * The instant `minutes` past `day`'s midnight on `zone`'s wall clock — the start a trainer means
 * when they pick "17:00 on 29.10". Found by correcting a UTC guess by the zone's reading of it;
 * twice, because the correction can itself cross an offset change. A reading that does not exist
 * (the hour a spring-forward skips) lands on the instant the zone reads just after it.
 */
export function zonedInstant(day: CivilDay, minutes: number, zone: string): number {
  let at = Date.parse(`${day}T00:00:00Z`) + minutes * MINUTE_MS;
  for (let step = 0; step < 2; step += 1) {
    const read = wallReading(at, zone);
    const dayOffset = (Date.parse(`${read.day}T00:00:00Z`) - Date.parse(`${day}T00:00:00Z`)) / DAY_MS;
    at -= (dayOffset * 1440 + read.minutes - minutes) * MINUTE_MS;
  }
  return at;
}

/**
 * The instant of `day`'s midnight on `zone`'s clock — where an occurrence with no hour is drawn,
 * at the top of its own day (the mobile grid's placement).
 */
export function zonedMidnight(day: CivilDay, zone: string): number {
  return zonedInstant(day, 0, zone);
}
